import React, { useCallback, useMemo } from 'react';
import { Table } from '~/shared/components';
import { nameColumnWidth } from '~/shared/constants';
import * as SparklineUtils from '~/shared/components/Table/charts/parseSparklineValues';
import {
  isNonExportableTableColumnType,
  normalizeTableExportHeaderLabel,
  type TableExportColumn,
} from '~/shared/components/Table/tableExport';

export default function TableChart({ data, headersData, isLoading, onClick, gridHeight, size }: any) {
  let headersDataKeys;
  let headersDataKeysDescription;
  let headersDataKeysWidth: any;
  let headersDataTypes;
  const omitHeaders: string[] = [];
  const childFirstColumnIndexes: number[] = [];
  const parentFirstColumnIndexes: number[] = [];
  const tableNewData = JSON.parse(JSON.stringify(data));
  let isDoubleHeaderHeight = false;
  let isDoubleSubHeaderHeight = false;

  const indexTitleWidth = data['index_title_width'] || nameColumnWidth;

  if (headersData.secondary_col_nodes) {
    const normalizeComparable = (value: string | undefined) => value?.replace(/\s+/g, ' ').trim().toLowerCase() ?? '';
    const pushOmitHeader = (...values: (string | undefined)[]) => {
      values.forEach(val => {
        if (val === undefined || val === null) return;
        const strVal = String(val);
        if (strVal && !omitHeaders.includes(strVal)) {
          omitHeaders.push(strVal);
        }
      });
    };

    headersDataKeys = headersData.col_nodes?.map((c: any) => c.name.replace(' ', '_'));
    headersDataKeysDescription = headersData.col_nodes?.map((c: any) => c.description);

    // TODO: revert dataKeysWidth
    headersDataKeysWidth = headersData.col_nodes?.map((c: any) => c.width);
    if (data?.title?.replace('\u05bf', '') === 'GS Basket Analytics') {
      headersDataKeysWidth = ['16', '36', '36', '12'];
    }
    headersDataTypes = headersData.col_nodes?.map((c: any) => c.type);

    const newColNodes: any = [];

    headersData.col_nodes.forEach((d: any, parentIndex: number) => {
      if (d.name.includes('\n')) isDoubleHeaderHeight = true;
      const sArray = headersData.secondary_col_nodes.filter((sd: any) => sd.parent_col === d.id);
      const count = sArray.length;
      const isOrderColumn = typeof d.name === 'string' && d.name.toLowerCase() === 'order';

      const rawParentName = typeof d.name === 'string' ? d.name : '';
      const comparableParentName = normalizeComparable(
        rawParentName ? rawParentName.replace('\n', ' ') : rawParentName,
      );
      if (count === 0) {
        const columnIndex = newColNodes.length;
        newColNodes.push(d);
        pushOmitHeader(rawParentName, d.id as string | undefined);
        if (d.name.includes('\n')) isDoubleSubHeaderHeight = true;
        if (!isOrderColumn) {
          childFirstColumnIndexes.push(columnIndex);
          parentFirstColumnIndexes.push(parentIndex);
        }
      } else {
        if (count === 1) {
          if (d.name.includes('\n')) isDoubleSubHeaderHeight = true;
        }
        const hasSingleChild = count === 1;
        const singleChild = hasSingleChild ? sArray[0] : undefined;
        const singleChildName =
          typeof singleChild?.name === 'string' ? singleChild.name.replace('\n', ' ') : singleChild?.name;
        const comparableSingleChildName = normalizeComparable(singleChildName);
        const shouldOmitParentHeader =
          hasSingleChild && comparableSingleChildName === comparableParentName && !isOrderColumn;

        if (!isOrderColumn && !shouldOmitParentHeader) {
          parentFirstColumnIndexes.push(parentIndex);
        }
        sArray.forEach((sd: any, i: number) => {
          const columnIndex = newColNodes.length;
          if (sd.name.includes('\n')) isDoubleSubHeaderHeight = true;
          const widthFromParent = typeof d.width === 'number' ? d.width : Number(d.width);
          const derivedWidth =
            sd.width !== undefined && sd.width !== null
              ? sd.width
              : Number.isFinite(widthFromParent) && count > 0
                ? widthFromParent / count
                : d.width;

          newColNodes.push({
            ...d,
            ...sd,
            id: sd.id,
            name: sd.name,
            type: sd.type || d.type,
            description: sd.description ?? d.description,
            parent_col: sd.parent_col,
            width: derivedWidth,
            color_scheme: sd.color_scheme ?? d.color_scheme,
            midpoint: sd.midpoint ?? d.midpoint,
            show_text: sd.show_text ?? d.show_text,
            alignment: sd.alignment ?? d.alignment ?? sd.ailgnment ?? d.ailgnment,
          });
          if (!isOrderColumn && i === 0) {
            childFirstColumnIndexes.push(columnIndex);
          }
        });

        if (shouldOmitParentHeader) {
          pushOmitHeader(rawParentName, d.id as string | undefined);
        }
      }
      tableNewData.col_nodes = newColNodes;
    });
  }
  // TODO: temp fix until BE removes \u05bf from response
  const tableTitle = tableNewData?.title?.replace('\u05bf', '');
  const tableSubtitle = tableNewData?.subtitle?.replace('\u05bf', '');
  const dataKeysRaw = tableNewData && tableNewData.col_nodes?.map((c: any) => c.name.replace(' ', '_'));
  const dataKeysDescription = tableNewData && tableNewData.col_nodes?.map((c: any) => c.description);

  // set specific color scheme from server to column, overrides "dataColors"
  const dataKeysColors = tableNewData && tableNewData.col_nodes?.map((c: any) => c.color_scheme);

  // TODO: revert dataKeysWidth
  let dataKeysWidth = tableNewData && tableNewData.col_nodes?.map((c: any) => c.width);
  if (data?.title?.replace('\u05bf', '') === 'GS Basket Analytics') {
    dataKeysWidth = ['16', 12, 12, 12, 12, 12, 12, 12];
  }

  // set array of 'show-hide-text' - effects horizontal stacked bar chart columns
  const dataKeysShowText = tableNewData && tableNewData.col_nodes?.map((c: any) => c.show_text);
  const dataTypes = tableNewData && tableNewData.col_nodes?.map((c: any) => c.type);
  const dataAlignments = tableNewData && tableNewData.col_nodes?.map((c: any) => c.alignment ?? c.ailgnment);
  const dataMidPoints = tableNewData && tableNewData.col_nodes?.map((c: any) => c.midpoint);
  const nameColArr =
    tableNewData && tableNewData.row_nodes?.map((c: any) => ({ name: c.name, id: c.id, description: c.description }));
  const numberSortedKeys = tableNewData &&
    tableNewData.col_nodes && ['order', ...tableNewData.col_nodes.filter((c: any) => c.type).map((c: any) => c.name)];

  // set global color scheme if column has none.
  const dataColors = tableNewData && tableNewData.color_scheme;

  const tableData = tableNewData?.data?.map((d: any, di: number) => {
    const returnObj: { [key: string]: any } = {};
    returnObj.order = di;
    returnObj.name = nameColArr[di]?.name;
    returnObj.description = nameColArr[di]?.description;
    returnObj.id = nameColArr[di]?.id;
    dataKeysRaw.forEach((dk: string, index: number) => {
      const calcMax = tableNewData?.data.reduce((acc: any, curr: any) => {
        if (typeof curr[index] === 'object') {
          const total = Math.abs(curr[index][0]) + Math.abs(curr[index][1]);
          return acc < Number(total) ? Number(total) : acc;
        }
        return acc < curr[index] ? curr[index] : acc;
      }, 0);
      const calcMin = tableNewData?.data.reduce((acc: any, curr: any) => (acc > curr[index] ? curr[index] : acc), 0);
      returnObj[`${dk}Max`] = calcMax > -calcMin ? calcMax : -calcMin;
      returnObj[`${dk}Min`] = calcMin < 0 ? (-calcMin < calcMax ? -calcMax : calcMin) : 0;
      returnObj[`${dk}Color`] = dataKeysColors[index] || dataColors;
      returnObj[`${dk}Width`] = dataKeysWidth[index];
      returnObj[`${dk}ShowText`] = dataKeysShowText[index];
    });
    d.forEach((c: any, i: number) => {
      const dataKey = dataKeysRaw[i];
      if (!dataKey) return;
      const dataTypeRaw = dataTypes?.[i];
      const normalizedDataType = typeof dataTypeRaw === 'string' ? dataTypeRaw.toLowerCase().trim() : '';
      const isLinkColumn = normalizedDataType === 'link';
      const isSparklineColumn = normalizedDataType === 'sparkline';
      if (isLinkColumn) {
        const linkValue = c;
        const linkTextCandidate =
          linkValue && typeof linkValue === 'object' && linkValue !== null && 'text' in linkValue
            ? (linkValue as { text?: unknown }).text
            : linkValue;
        const linkUrlCandidate =
          linkValue && typeof linkValue === 'object' && linkValue !== null && 'url' in linkValue
            ? (linkValue as { url?: unknown }).url
            : undefined;

        const linkText =
          linkTextCandidate === null || linkTextCandidate === undefined
            ? ''
            : typeof linkTextCandidate === 'string' || typeof linkTextCandidate === 'number'
              ? String(linkTextCandidate)
              : linkUrlCandidate && typeof linkUrlCandidate === 'string'
                ? linkUrlCandidate
                : '';
        const linkUrl =
          linkUrlCandidate === null || linkUrlCandidate === undefined
            ? undefined
            : typeof linkUrlCandidate === 'string' || typeof linkUrlCandidate === 'number'
              ? String(linkUrlCandidate)
              : undefined;

        returnObj[dataKey] = linkText;

        if (linkUrl !== undefined && linkUrl !== null && linkUrl !== '') {
          returnObj[`${dataKey}__url`] = String(linkUrl);
        } else {
          returnObj[`${dataKey}__url`] = undefined;
        }
        return;
      }

      if (c === null || c === undefined) {
        returnObj[dataKey] = '';
        return;
      }

      const resolvedSeries = SparklineUtils.resolveSparklineSeries(c);
      if (resolvedSeries && resolvedSeries.length > 1) {
        returnObj[`${dataKey}_Components`] = resolvedSeries;
        returnObj[`${dataKey}__series`] = SparklineUtils.formatSparklineSeries(resolvedSeries);
      }

      if (isSparklineColumn && resolvedSeries && resolvedSeries.length) {
        const lastValue = [...resolvedSeries].reverse().find(value => value !== null);
        returnObj[dataKey] = lastValue ?? '';
        return;
      }

      if (Array.isArray(c)) {
        if (!returnObj[`${dataKey}__series`]) {
          const values = c.map(value => {
            if (value === null || value === undefined) return '';
            const num = Number(value);
            return Number.isNaN(num) ? '' : num;
          });
          returnObj[dataKey] = values.join(',');
          return;
        }
        returnObj[dataKey] = returnObj[`${dataKey}__series`];
        return;
      }

      if (typeof c === 'number') {
        returnObj[dataKey] = c;
        return;
      }

      returnObj[dataKey] = String(c);
    });
    return returnObj;
  });

  const handleOnClick = useCallback(
    (e: any) => {
      const rowData = e?.rowData ?? {};
      const topicCandidates: string[] = [];
      const pushCandidate = (value: unknown) => {
        if (value === undefined || value === null) return;
        const text = String(value).trim();
        if (!text || topicCandidates.includes(text)) return;
        // Skip pure numerics / percentages — those are metric cells, not company labels.
        if (/^-?\d+(\.\d+)?%?$/.test(text)) return;
        topicCandidates.push(text);
      };

      // Prefer stable identifiers first so we resolve to real filter option ids.
      pushCandidate(e?.cellData);
      pushCandidate(rowData.id);
      pushCandidate(rowData.name);

      Object.entries(rowData).forEach(([key, value]) => {
        if (
          key === 'id' ||
          key === 'name' ||
          key === 'order' ||
          key === 'description' ||
          /Color|Max|Min|Width|ShowText|__|_Components/.test(key)
        ) {
          return;
        }
        if (/mimick|mmp|company|name|portfolio|asset|ticker/i.test(key)) {
          pushCandidate(value);
        }
      });

      const cellInfo = {
        source: 'value',
        topic: rowData.id ?? rowData.name ?? e?.cellData ?? topicCandidates[0],
        topicName: rowData.name ?? e?.cellData,
        cellData: e?.cellData,
        topicCandidates,
      };
      onClick(cellInfo);
    },
    [onClick],
  );

  const onHeaderClick = useCallback((e: any) => {
    return e?.target?.innerHTML;
  }, []);

  const hasRowNodes = tableNewData?.row_nodes?.length > 0;
  const dataKeysForTable = hasRowNodes ? ['order', 'name', ...(dataKeysRaw || [])] : ['order', ...(dataKeysRaw || [])];

  const sanitizeKey = (key: string) => key?.replace(/\s+/g, '_') ?? '';
  const dataColumnIds = dataKeysForTable.map((key, index) => `${sanitizeKey(String(key))}_${index}`);
  const dataKeysDescriptionForTable = hasRowNodes
    ? [undefined, undefined, ...(dataKeysDescription || [])]
    : [undefined, ...(dataKeysDescription || [])];
  const dataKeysColorsForTable = hasRowNodes
    ? ['#000', '#000', ...(dataKeysColors || [])]
    : ['#000', ...(dataKeysColors || [])];
  const dataKeysWidthForTable = hasRowNodes
    ? ['0', indexTitleWidth, ...(dataKeysWidth || [])]
    : ['0', ...(dataKeysWidth || [])];
  const dataTypesForTable = hasRowNodes ? ['empty', 'button', ...(dataTypes || [])] : ['empty', ...(dataTypes || [])];
  // Ensure the company/name column is clickable even when BE marks it as plain string.
  if (onClick) {
    const nameIdx = dataKeysForTable.findIndex(key => key === 'name');
    if (nameIdx >= 0) {
      dataTypesForTable[nameIdx] = 'button';
    } else {
      // A falsy/'string'/'text' declared type isn't proof the column actually holds text — BE
      // sometimes leaves numeric columns untyped too. Confirm against sampled cell values before
      // forcing ButtonRenderer on it, otherwise a numeric column can reach ButtonRenderer's
      // string-only cellData.toLowerCase() and crash (cellData.toLowerCase is not a function).
      const looksTextual = (key: string | undefined) => {
        if (!key || !tableData?.length) return false;
        return tableData.slice(0, 20).some((row: any) => {
          const value = row[key];
          if (value === undefined || value === null || value === '' || typeof value === 'number') return false;
          const text = String(value).trim();
          return text !== '' && !/^-?\d+(\.\d+)?%?$/.test(text);
        });
      };
      const firstTextIdx = dataTypesForTable.findIndex((type, index) => {
        if (dataKeysForTable[index] === 'order') return false;
        const normalized = String(type || '').toLowerCase();
        if (normalized && normalized !== 'string' && normalized !== 'text') return false;
        return looksTextual(dataKeysForTable[index]);
      });
      if (firstTextIdx >= 0) {
        dataTypesForTable[firstTextIdx] = 'button';
      }
    }
  }
  const dataAlignmentsForTable = hasRowNodes
    ? [undefined, undefined, ...(dataAlignments || [])]
    : [undefined, ...(dataAlignments || [])];
  const dataMidPointsForTable = hasRowNodes ? [0, 0, ...(dataMidPoints || [])] : [0, ...(dataMidPoints || [])];

  const dataOffset = hasRowNodes ? 2 : 1;
  const firstItemsSet = new Set(
    childFirstColumnIndexes
      .map((colIndex: number) => dataColumnIds[colIndex + dataOffset])
      .filter((id: string | undefined) => !!id),
  );

  if (hasRowNodes) {
    const firstDataColumnId = dataColumnIds[dataOffset];
    if (firstDataColumnId) firstItemsSet.add(firstDataColumnId);
  }

  const firstItems = Array.from(firstItemsSet);
  const lastColumnId = dataColumnIds[dataColumnIds.length - 1];
  const lastItems = lastColumnId ? [lastColumnId] : [];

  const hasHeaderRowNodes = headersData?.row_nodes?.length > 0;
  const hasSecondaryHeaders = headersData.secondary_col_nodes && headersData.secondary_col_nodes.length > 0;
  const headerKeysForTable = hasHeaderRowNodes
    ? ['order', 'name', ...(headersDataKeys || [])]
    : ['order', ...(headersDataKeys || [])];

  const headerColumnIds = headerKeysForTable.map((key, index) => `${sanitizeKey(String(key))}_${index}`);
  const headersDataKeysDescriptionForTable = hasHeaderRowNodes
    ? [undefined, undefined, ...(headersDataKeysDescription || [])]
    : [undefined, ...(headersDataKeysDescription || [])];
  const headersDataKeysWidthForTable = hasHeaderRowNodes
    ? ['0', indexTitleWidth, ...(headersDataKeysWidth || [])]
    : ['0', ...(headersDataKeysWidth || [])];
  const headersDataTypesForTable = hasHeaderRowNodes
    ? ['empty', 'button', ...(headersDataTypes || [])]
    : ['empty', ...(headersDataTypes || [])];
  const headerOffset = hasHeaderRowNodes ? 2 : 1;
  const headerFirstItemsSet = new Set(
    parentFirstColumnIndexes
      .map((colIndex: number) => headerColumnIds[colIndex + headerOffset])
      .filter((id: string | undefined) => !!id),
  );

  const headerFirstItems = Array.from(headerFirstItemsSet);
  const headerLastColumnId = headerColumnIds[headerColumnIds.length - 1];
  const headerLastItems = headerLastColumnId ? [headerLastColumnId] : [];

  const bodyFirstItemsForTable = hasSecondaryHeaders || hasRowNodes ? firstItems : [];
  const bodyLastItemsForTable = hasSecondaryHeaders ? lastItems : [];
  const headerFirstItemsForTable = hasSecondaryHeaders ? headerFirstItems : [];
  const headerLastItemsForTable = hasSecondaryHeaders ? headerLastItems : [];
  const bodyColumnIdsForTable = hasSecondaryHeaders ? dataColumnIds : undefined;
  const headerColumnIdsForTable = hasSecondaryHeaders ? headerColumnIds : undefined;

  const parentNameById = useMemo(
    () =>
      new Map<string, string>(
        (headersData.col_nodes ?? []).map((node: { id?: string; name?: string }) => [
          String(node.id ?? ''),
          normalizeTableExportHeaderLabel(node.name),
        ]),
      ),
    [headersData.col_nodes],
  );
  const omittedHeaderLabels = useMemo(
    () => new Set((omitHeaders ?? []).map(label => normalizeTableExportHeaderLabel(label).toLowerCase())),
    [omitHeaders],
  );
  const exportColumns = useMemo(() => {
    const columns: TableExportColumn[] = [];

    if (hasRowNodes) {
      columns.push({
        dataKey: 'name',
        label: normalizeTableExportHeaderLabel(data['index_title'] || 'Narrative'),
      });
    }

    tableNewData.col_nodes?.forEach((col: { id?: string; name?: string; parent_col?: string }, index: number) => {
      const dataKey = dataKeysRaw?.[index];
      const dataType = dataTypes?.[index];
      if (!dataKey || isNonExportableTableColumnType(dataType)) {
        return;
      }

      const label = normalizeTableExportHeaderLabel(col.name);
      let parentLabel: string | undefined;

      if (hasSecondaryHeaders && col.parent_col) {
        const parentName = parentNameById.get(String(col.parent_col));
        if (parentName) {
          const shouldOmitParent =
            parentName.toLowerCase() === label.toLowerCase() || omittedHeaderLabels.has(parentName.toLowerCase());
          if (!shouldOmitParent) {
            parentLabel = parentName;
          }
        }
      }

      columns.push({ dataKey, label, parentLabel });
    });

    return columns;
  }, [
    data,
    dataKeysRaw,
    dataTypes,
    hasRowNodes,
    hasSecondaryHeaders,
    omittedHeaderLabels,
    parentNameById,
    tableNewData.col_nodes,
  ]);

  if (!tableData || tableData?.length === 0) return <div>loading</div>;
  return (
    <Table
      size={size}
      hasRowNodes={hasRowNodes}
      isDoubleHeaderHeight={isDoubleHeaderHeight}
      isDoubleSubHeaderHeight={isDoubleSubHeaderHeight}
      hasHeaders={headersData.secondary_col_nodes && headersData.secondary_col_nodes.length > 0}
      headersDataKeys={headerKeysForTable}
      headersDataKeysDescription={headersDataKeysDescriptionForTable}
      headersDataKeysWidth={headersDataKeysWidthForTable}
      headersDataTypes={headersDataTypesForTable}
      omitHeaders={omitHeaders}
      headerFirstItems={headerFirstItemsForTable}
      headerLastItems={headerLastItemsForTable}
      headerColumnIds={headerColumnIdsForTable}
      data={tableData}
      dataKeys={dataKeysForTable}
      dataKeysDescription={dataKeysDescriptionForTable}
      dataKeysColors={dataKeysColorsForTable}
      dataKeysWidth={dataKeysWidthForTable}
      dataTypes={dataTypesForTable}
      dataAlignments={dataAlignmentsForTable}
      dataMidPoints={dataMidPointsForTable}
      defaultSortBy="order"
      dataColors={dataColors}
      title={tableTitle}
      subtitle={tableSubtitle}
      isLoading={isLoading}
      onHeaderClick={onHeaderClick}
      onRowClick={handleOnClick}
      gridHeight={gridHeight}
      numberSortedKeys={numberSortedKeys}
      indexTitle={data['index_title']}
      indexTitleWidth={indexTitleWidth}
      firstItems={bodyFirstItemsForTable}
      lastItems={bodyLastItemsForTable}
      columnIds={bodyColumnIdsForTable}
      exportColumns={exportColumns}
      includeExportParentHeaderRow={hasSecondaryHeaders}
    />
  );
}
